//product-form.tsx

import {
  MenuItem,
  Button,
  Box,
  InputAdornment,
  FormControl,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  IconButton,
} from "@mui/material";
import { useContext, useRef } from "react";
import type { JSX } from "react";
import { Crop } from "../models/crop";
import { Size } from "../models/size";
import {
  Formik,
  Form,
  FieldProps,
  FormikProps,
  FieldArray,
  FastField,
} from "formik";
import * as yup from "yup";
import { AppTextField } from "../components/text-field";
import { validationMessages } from "../utils/validation/messages";
import { ProductTypes } from "../services/api/products";
import { unitSystemLabel } from "../pages/crops-page";
import { UserContext } from "./app-routes";
import { FormPrompt } from "./form-prompt";
import { getAllInfoByISO } from "iso-country-currency";
import { v4 as uuidv4 } from "uuid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Divider from "@mui/material/Divider";
import { Pkg } from "../models/pkg";

export interface ProductFormValues {
  name: string;
  productCrops: { id: number; cropId: string; blend: string }[];
  productSizes: {
    id?: number;
    type: string;
    sizeId?: string;
    weight?: string;
    pkgId?: string;
    price: string;
  }[];
  notes: string;
  archived: boolean;
}

const validationSchema = yup.object({
  name: yup.string().trim().required(validationMessages.Required),
  productCrops: yup.array().of(
    yup.object().shape({
      cropId: yup.number().required(validationMessages.Required),
      blend: yup.string().nullable().notRequired(),
    }),
  ),
  productSizes: yup.array().of(
    yup.object().shape({
      type: yup.string().trim().required(validationMessages.Required),
      sizeId: yup.number().when("type", {
        is: ProductTypes.Cut,
        then: yup.number().required(validationMessages.Required),
      }),
      weight: yup.number().when("type", {
        is: ProductTypes.Cut,
        then: yup
          .number()
          .typeError(validationMessages.Number)
          .required(validationMessages.Required)
          .positive(validationMessages.Positive),
      }),
      price: yup.string().required(validationMessages.Required),
    }),
  ),
  notes: yup.string().max(5000),
  archived: yup.boolean(),
});

export default function ProductForm(props: {
  children: JSX.Element;
  initialValues: ProductFormValues;
  onSubmit: (values: ProductFormValues) => void;
  crops: Crop[];
  sizes: Size[];
  pkgs: Pkg[];
  saved: boolean;
}) {
  const context = useContext(UserContext);
  const grower = context.grower;

  if (!grower) {
    throw new Error("Invalid context");
  }

  const onSubmit = (values: ProductFormValues) => {
    values.productSizes.forEach((pS) => {
      if (pS.type === "tray") {
        pS.sizeId = undefined; // clear if was cut
        pS.weight = undefined; // clear if was cut
      }
    });
    props.onSubmit(values);
  };

  return (
    <Formik
      initialValues={props.initialValues}
      validationSchema={validationSchema}
      validate={(values) => {
        const errors: any = {};
        const isBlend = values.productCrops.length > 1;

        // ❌ trays + blends not allowed
        if (
          isBlend &&
          values.productSizes.some((s) => s.type === ProductTypes.Tray)
        ) {
          errors.productCrops = values.productCrops.map(() => ({
            blend: "Blended products cannot be sold as trays",
          }));
          return errors;
        }

        // ✅ blend validation
        if (isBlend) {
          const sum = values.productCrops.reduce(
            (t, c) => t + Number(c.blend || 0),
            0,
          );

          if (sum !== 100) {
            errors.productCrops = values.productCrops.map(() => ({
              blend: "Proportions do not total 100",
            }));
            return errors;
          }

          values.productCrops.forEach((c, i) => {
            if (!c.blend) {
              errors.productCrops ??= [];
              errors.productCrops[i] = {
                ...(errors.productCrops[i] || {}),
                blend: validationMessages.Required,
              };
            }
          });

          if (errors.productCrops) {
            return errors;
          }
        }

        return errors;
      }}
      onSubmit={onSubmit}
    >
      {(formProps: FormikProps<ProductFormValues>) => {
        return (
          <>
            <FormPrompt form={formProps} saved={props.saved} />
            <Box>
              <FormControl
                variant="standard"
                error={
                  formProps.touched.archived &&
                  Boolean(formProps.errors.archived)
                }
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="archived"
                      color="primary"
                      onChange={formProps.handleChange}
                      checked={formProps.values.archived}
                    />
                  }
                  label="Archived"
                />
                {formProps.touched.archived && formProps.errors.archived && (
                  <FormHelperText>{formProps.errors.archived}</FormHelperText>
                )}
              </FormControl>
            </Box>

            <Form noValidate>
              <Box sx={{ pb: 12 }}>
                <FastField name="name">
                  {(fieldProps: FieldProps) => (
                    <AppTextField
                      {...fieldProps}
                      autoFocus
                      label="Product Name"
                    />
                  )}
                </FastField>
                <div>
                  <FieldArray
                    name="productCrops"
                    render={(arrayHelpers) => {
                      const isBlend = formProps.values.productCrops.length > 1;

                      return (
                        <div>
                          {formProps.values.productCrops.map((c, i) => (
                            <div key={`${c.id}-${i}`}>
                              <Box
                                display="flex"
                                gap={2}
                                alignItems="flex-end"
                                sx={{
                                  flexDirection: { xs: "column", sm: "row" },
                                  alignItems: { xs: "stretch", sm: "flex-end" },
                                }}
                              >
                                {/* Crop */}
                                <Box
                                  sx={{
                                    flexBasis: isBlend ? "64.5%" : "100%",
                                    flexGrow: 0,
                                    flexShrink: 0,
                                    width: { xs: "100%", sm: "auto" },
                                  }}
                                >
                                  <FastField name={`productCrops[${i}].cropId`}>
                                    {(fieldProps: FieldProps) => (
                                      <AppTextField
                                        {...fieldProps}
                                        select
                                        label={
                                          "Crop" +
                                          (formProps.values.productCrops
                                            .length > 1
                                            ? ` ${i + 1}`
                                            : "")
                                        }
                                      >
                                        {props.crops
                                          .filter(
                                            (c) =>
                                              c.archivedAt === null ||
                                              props.initialValues.productCrops.some(
                                                (pC) =>
                                                  parseInt(pC.cropId) === c.id,
                                              ),
                                          )
                                          .sort((a, b) =>
                                            a.name.localeCompare(b.name),
                                          )
                                          .map((crop) => (
                                            <MenuItem
                                              key={crop.id}
                                              value={crop.id}
                                            >
                                              {crop.name}
                                            </MenuItem>
                                          ))}
                                      </AppTextField>
                                    )}
                                  </FastField>
                                </Box>

                                {/* Proportion (blend only) */}
                                {isBlend && (
                                  <Box
                                    sx={{
                                      flexBasis: "28.5%",
                                      flexGrow: 0,
                                      flexShrink: 0,
                                      width: { xs: "100%", sm: "auto" },
                                    }}
                                  >
                                    <FastField
                                      component={AppTextField}
                                      name={`productCrops[${i}].blend`}
                                      label="%"
                                    />
                                  </Box>
                                )}

                                {/* Trash can (blend only) */}
                                {isBlend && (
                                  <Box
                                    sx={{
                                      width: { xs: "100%", sm: "auto" },
                                      display: "flex",
                                      justifyContent: {
                                        xs: "flex-end",
                                        sm: "initial",
                                      },
                                    }}
                                  >
                                    <IconButton
                                      aria-label="Remove crop"
                                      onClick={() => {
                                        arrayHelpers.remove(i);

                                        // reset to 100% if only one remains
                                        if (
                                          formProps.values.productCrops
                                            .length === 2
                                        ) {
                                          formProps.setFieldValue(
                                            "productCrops[0].blend",
                                            "100",
                                          );
                                        }
                                      }}
                                    >
                                      <DeleteOutlineIcon />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                            </div>
                          ))}

                          <Box mt={2} sx={{ display: "flex", gap: 1 }}>
                            {!isBlend && (
                              <Button
                                color="secondary"
                                variant="contained"
                                onClick={() =>
                                  arrayHelpers.push({
                                    id: uuidv4(),
                                    cropId: "",
                                    blend: "",
                                  })
                                }
                              >
                                Create Blend
                              </Button>
                            )}

                            {isBlend && (
                              <>
                                <Button
                                  color="secondary"
                                  variant="contained"
                                  onClick={() =>
                                    arrayHelpers.push({
                                      id: uuidv4(),
                                      cropId: "",
                                      blend: "",
                                    })
                                  }
                                >
                                  Add Another Crop
                                </Button>
                              </>
                            )}
                          </Box>
                        </div>
                      );
                    }}
                  />
                  <Divider sx={{ my: 3, borderBottomWidth: 3, opacity: 1 }} />
                  <FieldArray
                    name="productSizes"
                    render={(arrayHelpers) => {
                      const cutSizes = formProps.values.productSizes
                        .map((ps, idx) => ({ ps, idx }))
                        .filter(({ ps }) => ps.type === ProductTypes.Cut);

                      const trayIndex = formProps.values.productSizes.findIndex(
                        (s) => s.type === ProductTypes.Tray,
                      );

                      return (
                        <div>
                          {/* CUT SIZES (no Type selector) */}
                          {cutSizes.map(({ ps, idx }) => (
                            <Box
                              key={`cut-${ps.id ?? idx}`}
                              display="flex"
                              gap={2}
                              alignItems="flex-end"
                              mb={0.1}
                              sx={{
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "stretch", sm: "flex-end" },
                              }}
                            >
                              {/* Size dropdown */}
                              <Box
                                flex={1.2}
                                sx={{
                                  width: { xs: "100%", sm: "auto" },
                                }}
                              >
                                <FastField name={`productSizes[${idx}].sizeId`}>
                                  {(fieldProps: FieldProps) => (
                                    <AppTextField
                                      {...fieldProps}
                                      select
                                      label="Size"
                                    >
                                      {props.sizes.map((size) => (
                                        <MenuItem key={size.id} value={size.id}>
                                          {size.name}
                                        </MenuItem>
                                      ))}
                                    </AppTextField>
                                  )}
                                </FastField>
                              </Box>

                              {/* Weight */}
                              <Box flex={1}>
                                <FastField
                                  component={AppTextField}
                                  name={`productSizes[${idx}].weight`}
                                  label={`Weight (${unitSystemLabel(grower)})`}
                                />
                              </Box>

                              {grower.Plan === "pro" && (
                                <Box flex={1.2}>
                                  <FastField
                                    name={`productSizes[${idx}].pkgId`}
                                  >
                                    {(fieldProps: FieldProps) => (
                                      <AppTextField
                                        {...fieldProps}
                                        select
                                        label="Package"
                                      >
                                        <MenuItem value=""> - </MenuItem>
                                        {props.pkgs
                                          .filter(
                                            (pkg) => pkg.archivedAt === null,
                                          )
                                          .map((pkg) => (
                                            <MenuItem
                                              key={pkg.id}
                                              value={pkg.id.toString()}
                                            >
                                              {pkg.name} - {pkg.size}
                                            </MenuItem>
                                          ))}
                                      </AppTextField>
                                    )}
                                  </FastField>
                                </Box>
                              )}

                              {/* Price */}
                              <Box
                                flex={1}
                                sx={{
                                  width: { xs: "100%", sm: "auto" },
                                }}
                              >
                                <FastField name={`productSizes[${idx}].price`}>
                                  {(fieldProps: FieldProps) => (
                                    <AppTextField
                                      {...fieldProps}
                                      label="Price"
                                      InputProps={{
                                        startAdornment: (
                                          <InputAdornment position="start">
                                            {
                                              getAllInfoByISO(grower.CountryISO)
                                                .symbol
                                            }
                                          </InputAdornment>
                                        ),
                                      }}
                                    />
                                  )}
                                </FastField>
                              </Box>

                              {/* Trash can */}
                              <Box
                                sx={{
                                  width: { xs: "100%", sm: "auto" },
                                  display: "flex",
                                  justifyContent: {
                                    xs: "flex-end",
                                    sm: "initial",
                                  },
                                }}
                              >
                                <IconButton
                                  aria-label="Remove size"
                                  onClick={() => arrayHelpers.remove(idx)}
                                >
                                  <DeleteOutlineIcon />
                                </IconButton>
                              </Box>
                            </Box>
                          ))}

                          {/* Add Size button (max 3 cut sizes) */}
                          <Box mt={2} sx={{ display: "flex", gap: 1 }}>
                            <Button
                              color="secondary"
                              variant="contained"
                              disabled={cutSizes.length >= 3}
                              onClick={() =>
                                arrayHelpers.push({
                                  type: ProductTypes.Cut,
                                  sizeId: "",
                                  weight: "",
                                  price: "",
                                })
                              }
                            >
                              Add Size
                            </Button>
                          </Box>

                          <Divider
                            sx={{ my: 3, borderBottomWidth: 3, opacity: 1 }}
                          />
                          {/* LIVE TRAY TOGGLE */}
                          <Box mb={1} mt={1}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={trayIndex !== -1}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      if (trayIndex === -1) {
                                        arrayHelpers.push({
                                          type: ProductTypes.Tray,
                                          price: "",
                                        });
                                      }
                                    } else {
                                      if (trayIndex !== -1) {
                                        arrayHelpers.remove(trayIndex);
                                      }
                                    }
                                  }}
                                />
                              }
                              label="Sell as Live Tray"
                            />
                          </Box>

                          {/* LIVE TRAY PRICE */}
                          {trayIndex !== -1 && (
                            <Box
                              sx={{ width: { xs: "100%", sm: "40%" }, mb: 2 }}
                            >
                              <FastField
                                name={`productSizes[${trayIndex}].price`}
                              >
                                {(fieldProps: FieldProps) => (
                                  <AppTextField
                                    {...fieldProps}
                                    label="Live Tray Price"
                                    InputProps={{
                                      startAdornment: (
                                        <InputAdornment position="start">
                                          {
                                            getAllInfoByISO(grower.CountryISO)
                                              .symbol
                                          }
                                        </InputAdornment>
                                      ),
                                    }}
                                  />
                                )}
                              </FastField>
                            </Box>
                          )}

                          <Divider
                            sx={{ my: 3, borderBottomWidth: 3, opacity: 1 }}
                          />

                          {/* Notes */}
                          <FastField name="notes">
                            {(fieldProps: FieldProps) => (
                              <AppTextField
                                {...fieldProps}
                                multiline
                                label="Notes"
                                rows={4}
                              />
                            )}
                          </FastField>
                        </div>
                      );
                    }}
                  />
                </div>
              </Box>
              {props.children}
            </Form>
          </>
        );
      }}
    </Formik>
  );
}
